import { useEffect, useMemo, useState } from 'react';
import { ActiveOrderData, ApiOrder } from '../types';
import { Res } from '../state/api-resources';
import { useApi } from '../state/api';
import { useConfig } from '../config';

/**
 * The customer's most recent orders, with the lines of each.
 *
 * `me/orders.json` is every real order newest first - it drops the denied, the deleted and the
 * abandoned payment sessions, and nothing else - so what comes back is the latest orders rather
 * than the ones still in progress. This was called useActiveOrders, which it never was.
 *
 * The list endpoint carries no lines, and the rows show what was bought, so each order is read
 * once more for them.
 */
export const useLatestOrders = () => {
	const {
		data: ordersSummary,
		isLoading: summaryLoading,
		error: summaryError,
	} = useApi(Res.orders, {
		limit: 10,
		offset: 0,
	});

	const [detailedData, setDetailedData] = useState<Record<number, ApiOrder>>({});
	const [detailedLoading, setDetailedLoading] = useState(false);
	const [detailedError, setDetailedError] = useState<Error | null>(null);
	const { api } = useConfig();

	useEffect(() => {
		if (!ordersSummary?.orders?.length) return;

		let isCancelled = false;
		setDetailedLoading(true);
		setDetailedError(null);

		const fetchDetails = async () => {
			try {
				const responses = await Promise.all(
					ordersSummary.orders.map((order) =>
						fetch(api + Res.order.uri({ orderId: order.id.toString() }), {
							credentials: 'include',
						}).then((res) => {
							if (!res.ok) throw new Error(`Failed to fetch order ${order.id}`);
							return res.json();
						})
					)
				);

				if (!isCancelled) {
					const map: Record<number, ApiOrder> = {};
					ordersSummary.orders.forEach((order, idx) => {
						map[order.id] = responses[idx];
					});
					setDetailedData(map);
				}
			} catch (err) {
				if (!isCancelled) setDetailedError(err as Error);
			} finally {
				if (!isCancelled) setDetailedLoading(false);
			}
		};

		fetchDetails();

		return () => {
			isCancelled = true;
		};
	}, [ordersSummary]);

	const latestOrders = useMemo<ActiveOrderData[] | null>(() => {
		if (!ordersSummary) return null;
		const result: ActiveOrderData[] = [];

		for (const order of ordersSummary.orders) {
			const details = detailedData[order.id];
			if (!details) return null; // still loading

			result.push({
				id: order.id,
				displayId: order.displayId,
				datePurchased: order.datePurchased,
				delivery: order.delivery,
				payment: order.payment,
				totals: order.totals,
				status: order.status,
				items: details.items,
			});
		}

		return result;
	}, [ordersSummary, detailedData]);

	return {
		data: latestOrders,
		isLoading: summaryLoading || detailedLoading,
		error: summaryError || detailedError,
	};
};
